import { useCallback, useEffect, useRef } from 'react'
import { isInputFocused } from '../../dom'

export interface KeyConfig {
  key: string
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
  metaKey: boolean
}

function getKeys(input: string): string[] {
  const tokens: string[] = []
  let i = 0
  const n = input.length

  while (i < n) {
    const char = input[i]
    if (!char) break

    if (/\w/.test(char)) {
      // Capture word tokens
      const start = i
      while (i < n && /\w/.test(input[i] ?? '')) i++

      tokens.push(input.slice(start, i))
    } else if (char === '+') {
      const plusStart = i
      while (i < n && input[i] === '+') i++

      const plusCount = i - plusStart

      if (plusCount >= 2 || i === n) {
        // Multiple '+'s act as a separator
        tokens.push('+')
      }
      // Single '+' between letters is ignored
    } else {
      // Ignore any other characters
      i++
    }
  }

  return tokens.map(token => token.toLowerCase())
}

export function parseKeyString(input: string): KeyConfig {
  const keys = getKeys(input)
  if (keys.length === 0) throw new Error('Invalid key string')

  let key = ''
  let ctrlKey = false,
    shiftKey = false,
    altKey = false,
    metaKey = false
  for (const k of keys) {
    if (k === 'ctrl') ctrlKey = true
    else if (k === 'shift') shiftKey = true
    else if (k === 'alt') altKey = true
    else if (k === 'meta') metaKey = true
    else key = k
  }

  return { key, ctrlKey, shiftKey, altKey, metaKey }
}

export interface UseHotkeyOptions {
  /** Disable the hotkey */
  disabled?: boolean
  /** Skip if function returns true */
  skipIf?(evt: KeyboardEvent): boolean
  /** Skip if any input, textarea or contenteditable element is in focus */
  skipIfInputFocused?: boolean
  /** If not on mac, the "meta" key will be treated as ctrl */
  metaAsCtrlOnWindows?: boolean
}

export function useHotkey(
  key: string,
  callback: (evt: KeyboardEvent) => void,
  options: UseHotkeyOptions = {}
) {
  const callbackRef = useRef(callback)
  const optionsRef = useRef(options)
  const keyRef = useRef<KeyConfig>(parseKeyString(key))

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    optionsRef.current = options
  }, [options])

  useEffect(() => {
    keyRef.current = parseKeyString(key)
  }, [key])

  const matchKey = useCallback((evt: KeyboardEvent): boolean => {
    const { key, shiftKey, altKey, metaKey } = keyRef.current
    let { ctrlKey } = keyRef.current

    if (optionsRef.current.metaAsCtrlOnWindows) {
      const isMac = navigator.userAgent.indexOf('Mac OS X') !== -1
      if (!isMac && metaKey) ctrlKey = true
    }

    return (
      evt.key.toLowerCase() === key &&
      evt.ctrlKey === ctrlKey &&
      evt.shiftKey === shiftKey &&
      evt.altKey === altKey &&
      evt.metaKey === metaKey
    )
  }, [])

  useEffect(() => {
    const options = optionsRef.current
    if (options.disabled) return

    function handler(evt: KeyboardEvent) {
      if (!matchKey(evt)) return
      if (!!options.skipIfInputFocused && isInputFocused()) return
      if (options.skipIf?.(evt)) return

      callbackRef.current(evt)
    }

    window.addEventListener('keydown', handler)

    return () => window.removeEventListener('keydown', handler)
  }, [key, matchKey])
}
